<script lang="ts" setup>
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import SpinnerTail from '@/vue/atoms/spinner-tail.vue';
import type {RouteLocationRaw} from 'vue-router';

withDefaults(defineProps<{
  label?: string;
  to?: RouteLocationRaw;
  loading?: boolean;
  icon?: string;
  title?: string;
  disable?: boolean;
  type?: 'button' | 'submit';
  theme?: 'blue' | 'red' | 'green' | 'grey' | 'white' | 'light-white' | 'inverse' | 'plain'
    | 'plain-underline' | 'outline' | 'enroll';
  paddings?: 'p1-4-1-3' | 'p2-5' | 'p3-1' | 'p3-2' | 'p3-7' | 'none';
  iconPosition?: 'left' | 'right';
  ariaLabel?: string;
  compact?: boolean;
  /**
   * Glyph size. `default` leaves the icon at its intrinsic size, which is what every caller has
   * always got; `sm` draws it at 16px, for the smaller buttons of the Today tiles. It is a prop
   * rather than something the caller overrides from outside because `.icon-holder` is this
   * component's own markup — reaching into it from a consumer breaks the moment it is renamed.
   */
  iconSize?: 'default' | 'sm';
}>(), {
  loading: false,
  type: 'button',
  theme: 'blue',
  paddings: 'p3-7',
  disable: false,
  iconPosition: 'right',
  compact: false,
  iconSize: 'default',
});
</script>

<template>
  <q-btn
    rounded
    :round="!label"
    :label="label"
    :title="title"
    :loading="loading"
    :disable="disable"
    :type="type"
    :to="to"
    :class="[theme, paddings, {'icon-only': !label, 'compact': compact}, `icon-${iconPosition}`, `icon-size-${iconSize}`]"
    :flat="true"
    class="balm-btn"
  >
    <div v-if="ariaLabel" class="sr-only">
      {{ ariaLabel }}
    </div>
    <div v-if="icon" class="icon-holder">
      <svg-illustration :name="icon"/>
    </div>
    <template #loading>
      <spinner-tail :size="24"/>
    </template>
  </q-btn>
</template>
<style lang="scss" scoped>

@mixin paddings($top, $right, $bottom: $top, $left: $right) {
  &.icon-only {
    padding: $top $top;
  }

  &:not(.icon-only) {
    padding: $top $right $bottom $left;
  }
}

.icon-holder {
  // Keep the icon at its intrinsic size: without this the holder shrinks under
  // flex pressure (long labels + narrow buttons, e.g. the German verify-email
  // actions on mobile) and the svg's max-width:100% collapses the arrow.
  flex-shrink: 0;
}

.icon-left .icon-holder {
  order: 1;
  margin-right: 10px;
}

.icon-right .icon-holder {
  order: 3;
  margin-left: 10px;
}

.icon-only.icon-right .icon-holder {
  margin: 0;
}

// Only reachable through `icon-size="sm"`, so the default path is untouched: with no class, the
// glyph keeps the intrinsic size every existing caller renders it at.
.icon-size-sm .icon-holder {
  width: 16px;
  height: 16px;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.balm-btn {
  flex-shrink: 0;
  @include font-body-bold;
  @include focus-visible;
  border-radius: var(--button-border-radius);
  width: fit-content;
  
  // Add subtle visual cues for flat design accessibility
  transition: all 0.2s ease-in-out;
  
  // Hover states for better discoverability
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
  
  // Active states for tactile feedback
  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.95);
  }
  
  // Disabled state clarity
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  // Enhanced focus state for flat design
  &:focus-visible {
    @include focus-visible-default-styling;
    // Add subtle shadow for depth without breaking flat design
    box-shadow: 0 0 0 4px rgba(var(--Primary-primary-color-1-rgb), 0.2);
  }
  
  :deep(.q-btn__content) {
    display: flex;
    flex-direction: row;
    // A long label must wrap within its own box — never push the icon to
    // its own line below the text.
    flex-wrap: nowrap;

    > span:first-child {
      order: 2;
    }
  }

  &.white {
    background-color: var(--Neutral-UI-neutral-ui-0);
  }

  // Solid high-contrast fill: the neutral pair inverted, so it stays the strongest button on the
  // surface it sits on in either theme. The two custom properties are the extension point for a
  // caller whose surface does not follow the scheme — the Today video tile paints a fixed dark
  // frame, so it overrides the pair rather than inheriting a button that would vanish into it.
  &.inverse {
    color: var(--balm-btn-inverse-ink, var(--Neutral-UI-neutral-ui-0));
    background-color: var(--balm-btn-inverse-surface, var(--Neutral-UI-neutral-ui-100));
  }

  &.light-white {
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
    background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  }

  &.grey {
    background-color: var(--Neutral-UI-neutral-ui-20);
  }

  &.blue {
    color: var(--Neutral-UI-neutral-ui-100);
    background-color: var(--Secondary-secondary-color-1);
    // Ensure 4.5:1 contrast ratio minimum
  }

  &.green {
    color: var(--Neutral-UI-neutral-ui-100);
    background-color: var(--Secondary-secondary-color-2);
    // Ensure 4.5:1 contrast ratio minimum
  }

  &.enroll {
    color: var(--Light-Scheme-Functional-wl-current-text);
    background-color: var(--Light-Scheme-Functional-wl-current-surface);
  }

  &.red {
    color: var(--Neutral-UI-neutral-ui-100);
    background-color: var(--Secondary-secondary-color-3);
  }

  &.plain {
    background-color: transparent;
  }

  // Quiet text link (design-system secondary action): renders as inline underlined text, NOT a
  // pill button — no fixed button height, no rounded background/silhouette on hover.
  &.plain-underline {
    color: var(--Neutral-UI-neutral-ui-60);
    text-decoration: underline;
    @include font-body();
    min-height: 0;
    min-width: 0;
    border-radius: 0;

    &:hover:not(:disabled) {
      transform: none;
      filter: none;
    }

    :deep(.q-btn__wrapper) {
      min-height: 0;
      padding: var(--Spacing-spacing-1) var(--Spacing-spacing-2);
    }

    // Suppress Quasar's rounded hover/focus overlay so no button silhouette shows behind the link.
    :deep(.q-focus-helper) {
      display: none;
    }
  }

  &.outline {
    color: var(--Functional-wl-current-surface);
    border: 2px solid var(--Neutral-UI-neutral-ui-100);
  }

  &.p3-7 {
    @include paddings(var(--Spacing-spacing-3), var(--Spacing-spacing-7));
  }

  &.p2-5 {
    @include paddings(var(--Spacing-spacing-2), var(--Spacing-spacing-5));
  }

  &.p3-1 {
    @include paddings(var(--Spacing-spacing-3), var(--Spacing-spacing-1));
  }

  &.p3-2 {
    @include paddings(var(--Spacing-spacing-3), var(--Spacing-spacing-2));
  }

  &.p1-4-1-3 {
    @include paddings(var(--Spacing-spacing-1), var(--Spacing-spacing-4), var(--Spacing-spacing-1), var(--Spacing-spacing-3));
  }

  min-height: 44px;
  min-width: 44px;

  &.icon-only {
    min-height: 44px;
    min-width: 44px;
  }

  &.compact {
    min-height: unset !important;
    min-width: unset !important;
    height: 28px;
    padding: 0 6px !important;

    @include screen-md {
      height: 40px;
      padding: 0 var(--Spacing-spacing-3) !important;
    }

    @include screen-lg {
      height: 56px;
      padding: var(--Spacing-spacing-3) var(--Spacing-spacing-6) !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    
    &:hover:not(:disabled) {
      transform: none;
    }
    
    &:active:not(:disabled) {
      transform: none;
    }
  }
}
</style>
